import React from "react";
import {
  Eye, EyeOff, Lock, MonitorSmartphone, PanelLeft, Smartphone,
  LayoutDashboard, Settings as SettingsIcon, ArrowLeftRight,
} from "lucide-react";
import { PAGE_STATES } from "../hooks/useUserPermissionConfig";

// Priority order for mobile bottom-bar content pages (after pinned Dashboard).
const MOBILE_TAB_PRIORITY = [
  "employees", "leaves", "messages", "attendance", "calendar",
  "documents", "reports", "advancements", "changesPage", "payroll", "configurations",
];

/**
 * Compose the mock phone bottom bar from the current page states.
 * NOTE: this algorithm mirrors the mobile app's src/navigation/tabComposition.ts —
 * keep the two in sync:
 *   1. "Dashboard" is always pinned first.
 *   2. Content pages in MOBILE_TAB_PRIORITY order where state === VISIBLE_ACCESSIBLE.
 *   3. Slice to max 5 tabs total.
 *   4. If fewer than 4 tabs, append "Settings".
 *   5. The round view-switcher FAB sits at the visual center and never counts
 *      toward the 5.
 */
const composeMobileTabs = (pageStates, registryPages, language, t) => {
  const labelOf = (key) => {
    const page = (registryPages || []).find((p) => p.key === key);
    return page?.labels?.[language] || page?.labels?.en || key;
  };
  const tabs = [{ key: "__dashboard__", label: t.dashboardLabel }];
  MOBILE_TAB_PRIORITY.forEach((key) => {
    if (pageStates[key] === PAGE_STATES.FULL) {
      tabs.push({ key, label: labelOf(key) });
    }
  });
  const sliced = tabs.slice(0, 5);
  if (sliced.length < 4) sliced.push({ key: "__settings__", label: t.settingsLabel });
  return sliced;
};

const TAB_ICON = { __dashboard__: LayoutDashboard, __settings__: SettingsIcon };

/**
 * Right panel: live preview, driven purely by local optimistic state so it
 * updates instantly on every toggle.
 *  (a) mock web sidebar — full/locked/hidden(ghosted) treatment per page
 *  (b) mock phone bottom bar — see composeMobileTabs above
 */
const PreviewPanel = ({ registry, pageStates, theme, language, t }) => {
  const headColor = theme === "dark" ? "#f1f5f9" : "#1e293b";
  const subColor = theme === "dark" ? "#94a3b8" : "#64748b";
  const pages = registry?.pages || [];

  const mobileTabs = composeMobileTabs(pageStates, pages, language, t);
  // FAB sits at the visual center: split tabs around it
  const half = Math.ceil(mobileTabs.length / 2);
  const leftTabs = mobileTabs.slice(0, half);
  const rightTabs = mobileTabs.slice(half);

  const sectionLabel = (Icon, text) => (
    <div style={{
      display: "flex", alignItems: "center", gap: "6px",
      fontSize: "11px", fontWeight: 700, letterSpacing: "0.06em",
      textTransform: "uppercase", color: subColor, marginBottom: "10px",
    }}>
      <Icon size={12} />
      {text}
    </div>
  );

  const renderTab = ({ key, label }) => {
    const Icon = TAB_ICON[key] || null;
    return (
      <div key={key} style={{
        flex: 1, minWidth: 0, display: "flex", flexDirection: "column",
        alignItems: "center", gap: "3px", padding: "2px 0",
      }}>
        {Icon
          ? <Icon size={14} style={{ color: subColor }} />
          : <span style={{
              width: "14px", height: "14px", borderRadius: "4px",
              background: theme === "dark" ? "rgba(148,163,184,0.35)" : "rgba(100,116,139,0.3)",
            }} />}
        <span style={{
          fontSize: "8.5px", fontWeight: 600, color: subColor, maxWidth: "100%",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {label}
        </span>
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
        <MonitorSmartphone size={16} style={{ color: "#2e7cba" }} />
        <h4 style={{ margin: 0, fontSize: "15px", fontWeight: 700, color: headColor }}>
          {t.previewTitle}
        </h4>
      </div>

      {/* ── (a) mock web sidebar ── */}
      {sectionLabel(PanelLeft, t.webPreview)}
      <div style={{
        borderRadius: "14px",
        padding: "12px 10px",
        marginBottom: "24px",
        border: theme === "dark"
          ? "1px solid rgba(148,163,184,0.14)"
          : "1px solid rgba(15,23,42,0.07)",
        background: theme === "dark" ? "rgba(15,23,42,0.6)" : "rgba(248,250,252,0.9)",
      }}>
        {pages.map((page) => {
          const state = pageStates[page.key] || PAGE_STATES.HIDDEN;
          const isHidden = state === PAGE_STATES.HIDDEN;
          const isLocked = state === PAGE_STATES.LOCKED;
          const label = page.labels?.[language] || page.labels?.en || page.key;
          return (
            <div
              key={page.key}
              style={{
                display: "flex", alignItems: "center", gap: "8px",
                padding: "7px 10px", borderRadius: "9px",
                transition: "opacity 0.25s ease",
                opacity: isHidden ? 0.35 : 1, // hidden pages are ghosted, not omitted
              }}
            >
              {isHidden
                ? <EyeOff size={13} style={{ color: subColor, flexShrink: 0 }} />
                : isLocked
                  ? <Lock size={13} style={{ color: "#d97706", flexShrink: 0 }} />
                  : <Eye size={13} style={{ color: "#059669", flexShrink: 0 }} />}
              <span style={{
                fontSize: "12.5px", fontWeight: isHidden ? 400 : 600,
                color: isHidden ? subColor : headColor,
                textDecoration: isHidden ? "line-through" : "none",
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                flex: 1, minWidth: 0,
              }}>
                {label}
              </span>
              {isLocked && (
                <span style={{
                  fontSize: "9px", fontWeight: 700, padding: "1px 6px",
                  borderRadius: "999px", flexShrink: 0,
                  color: "#d97706",
                  background: theme === "dark" ? "rgba(217,119,6,0.15)" : "rgba(217,119,6,0.1)",
                }}>
                  {t.lockedTag}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* ── (b) mock phone bottom bar ── 
      {sectionLabel(Smartphone, t.mobilePreview)}
      <div style={{
        borderRadius: "22px",
        padding: "10px",
        border: theme === "dark"
          ? "1px solid rgba(148,163,184,0.2)"
          : "1px solid rgba(15,23,42,0.1)",
        background: theme === "dark" ? "rgba(15,23,42,0.75)" : "#ffffff",
        boxShadow: theme === "dark"
          ? "0 6px 20px rgba(0,0,0,0.35)"
          : "0 6px 20px rgba(0,0,0,0.08)",
      }}>
        {/* faux screen area */}
        {/* <div style={{
          height: "54px", borderRadius: "12px", marginBottom: "8px",
          background: theme === "dark" ? "rgba(30,41,59,0.5)" : "rgba(241,245,249,0.9)",
        }} />
        {/* bottom bar */}
        {/* <div style={{
          display: "flex", alignItems: "center", gap: "4px",
          padding: "6px 4px",
          borderRadius: "16px",
          background: theme === "dark" ? "rgba(30,41,59,0.8)" : "rgba(248,250,252,1)",
          border: theme === "dark"
            ? "1px solid rgba(148,163,184,0.12)"
            : "1px solid rgba(15,23,42,0.05)",
        }}> */}
          {/* {leftTabs.map(renderTab)} */}
          {/* round view-switcher FAB — visually centered, never a tab */}
          {/* <div style={{
            width: "34px", height: "34px", borderRadius: "50%", flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "linear-gradient(135deg,#2e7cba,#1e5a96)",
            boxShadow: "0 4px 10px rgba(46,124,186,0.4)",
            margin: "0 2px",
            marginTop: "-16px",
            border: `3px solid ${theme === "dark" ? "#0f172a" : "#ffffff"}`,
          }}> */}
            {/* <ArrowLeftRight size={14} style={{ color: "#ffffff" }} />
          </div>
          {rightTabs.map(renderTab)}
        </div> */} 
      {/* </div>  */}
    </div>
  );
};

export default PreviewPanel;
